// SuperAdminAccess.js — iter202
//
// Single-page onboarding hub for Super Admin:
//   • Landing URL + Copy button
//   • Login URL + Copy button
//   • Create User (email + password)
//   • Reset Password (for any existing manager)
//   • Disable / Reactivate user (status toggle)
//
// Uses ONLY the existing admin endpoints — no new backend code:
//   POST   /api/admin/managers
//   POST   /api/admin/managers/{id}/reset-password
//   PUT    /api/admin/managers/{id}/status
//   POST   /api/admin/managers/{id}/restore
//   GET    /api/admin/managers
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Copy, Check, Link2, UserPlus, KeyRound, UserX, UserCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminAccess() {
  const { getHeaders } = useAuth();
  const origin  = window.location.origin;
  const landingUrl = `${origin}/welcome`;
  const loginUrl   = `${origin}/login`;

  const [managers, setManagers]   = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(null);
  const [msg, setMsg] = useState(null);

  // Create user form
  const [cu, setCu] = useState({ name:'', email:'', password:'' });
  const [creating, setCreating] = useState(false);

  async function refresh() {
    if (!API) return;
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/api/admin/managers?status_filter=all`, { headers: getHeaders() });
      const data = r.data?.data || r.data?.managers || r.data || [];
      setManagers(Array.isArray(data) ? data : (data.managers || []));
    } catch (e) {
      setMsg({ kind:'error', text: `Could not load users: ${e?.response?.data?.detail || e.message}` });
    }
    finally { setRefreshing(false); }
  }
  useEffect(() => { refresh(); }, []);  // eslint-disable-line

  async function copy(label, text) {
    try { await navigator.clipboard.writeText(text); }
    catch { /* fallback */ }
    setCopied(label); setTimeout(() => setCopied(null), 1500);
  }

  async function createUser(e) {
    e.preventDefault();
    if (!cu.email.trim() || !cu.password.trim()) {
      setMsg({ kind:'error', text:'Email and password are required.' });
      return;
    }
    if (cu.password.length < 6) {
      setMsg({ kind:'error', text:'Password must be at least 6 characters.' });
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/api/admin/managers`,
        { name: cu.name || cu.email, email: cu.email,
          password: cu.password, outlet_ids: [], role: 'studio_user' },
        { headers: getHeaders() });
      setMsg({ kind:'ok',
               text:`User created. Share landing URL + credentials with the consultant.` });
      setCu({ name:'', email:'', password:'' });
      refresh();
    } catch (e) {
      setMsg({ kind:'error',
               text: e?.response?.data?.detail || e.message });
    } finally { setCreating(false); }
  }

  async function resetPwd(mgr) {
    const np = window.prompt(`New password for ${mgr.email || mgr.name}:`);
    if (!np) return;
    if (np.length < 6) { alert('Password must be ≥ 6 chars.'); return; }
    try {
      await axios.post(`${API}/api/admin/managers/${mgr.id || mgr._id}/reset-password`,
        { new_password: np }, { headers: getHeaders() });
      setMsg({ kind:'ok', text:`Password reset for ${mgr.email}. Share the new password securely.` });
    } catch (e) {
      setMsg({ kind:'error', text: e?.response?.data?.detail || e.message });
    }
  }

  async function toggleStatus(mgr) {
    const id = mgr.id || mgr._id;
    const isActive = (mgr.status || 'active') === 'active';
    try {
      if (isActive) {
        await axios.put(`${API}/api/admin/managers/${id}/status`,
          { status: 'inactive' }, { headers: getHeaders() });
      } else {
        await axios.post(`${API}/api/admin/managers/${id}/restore`,
          {}, { headers: getHeaders() });
      }
      refresh();
    } catch (e) {
      setMsg({ kind:'error', text: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6"
         data-testid="super-access-root"
         style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <header>
        <h1 className="text-2xl font-bold text-emerald-900">Studio Access</h1>
        <p className="text-sm text-slate-600">
          Share the landing & login URLs with consultants, then create their
          accounts here. Anything below uses your existing tenant — nothing is
          duplicated.
        </p>
      </header>

      {msg && (
        <div className={`px-4 py-2 rounded text-sm ${
          msg.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                             : 'bg-rose-50 text-rose-800 border border-rose-200'
        }`} data-testid="super-access-msg">{msg.text}</div>
      )}

      {/* ── Public URLs ─────────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-emerald-700" /> Public URLs
        </CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <UrlRow label="Landing Page URL" url={landingUrl}
                  onCopy={() => copy('landing', landingUrl)}
                  copied={copied === 'landing'}
                  testid="super-access-landing" />
          <UrlRow label="Login URL" url={loginUrl}
                  onCopy={() => copy('login', loginUrl)}
                  copied={copied === 'login'}
                  testid="super-access-login" />
          <p className="text-xs text-slate-500">
            Share the <b>Landing URL</b> with new consultants — it gives them
            a premium first impression before they sign in. Power users can
            bookmark the <b>Login URL</b> directly.
          </p>
        </CardContent>
      </Card>

      {/* ── Create User ─────────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-emerald-700" /> Create User
        </CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={createUser}
                className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end"
                data-testid="super-access-create-form">
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Name (optional)</label>
              <Input value={cu.name}
                     onChange={(e) => setCu({...cu, name:e.target.value})}
                     placeholder="Consultant name"
                     data-testid="super-access-create-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Email / Username *</label>
              <Input type="email" value={cu.email}
                     onChange={(e) => setCu({...cu, email:e.target.value})}
                     placeholder="consultant@firm.com"
                     data-testid="super-access-create-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-slate-500">Password *</label>
              <Input type="text" value={cu.password}
                     onChange={(e) => setCu({...cu, password:e.target.value})}
                     placeholder="≥ 6 chars"
                     data-testid="super-access-create-password" />
            </div>
            <Button type="submit" disabled={creating}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="super-access-create-submit">
              {creating ? 'Creating…' : 'Create user'}
            </Button>
          </form>
          <p className="text-xs text-slate-500 mt-3">
            The email becomes the username. Share the password through a
            secure channel — never email it in plaintext.
          </p>
        </CardContent>
      </Card>

      {/* ── Users list ─────────────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle className="flex items-center justify-between">
          <span>Existing Users ({managers.length})</span>
          <Button size="sm" variant="outline" onClick={refresh}
                  disabled={refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="super-access-users-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-3">Email</th>
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Role</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {managers.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-slate-500">
                  No users yet. Create the first consultant above.
                </td></tr>
              )}
              {managers.map((m) => {
                const id = m.id || m._id;
                const active = (m.status || 'active') === 'active';
                return (
                  <tr key={id} className="border-b border-slate-100"
                      data-testid={`super-access-user-row-${m.email}`}>
                    <td className="py-2 pr-3 font-medium">{m.email}</td>
                    <td className="py-2 pr-3 text-slate-600">{m.name || '—'}</td>
                    <td className="py-2 pr-3 text-slate-600">{m.role || 'manager'}</td>
                    <td className="py-2 pr-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        active ? 'bg-emerald-100 text-emerald-800' :
                                  'bg-rose-100 text-rose-800'
                      }`}>{active ? 'active' : 'disabled'}</span>
                    </td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => resetPwd(m)}
                                data-testid={`super-access-reset-${m.email}`}>
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset password
                        </Button>
                        <Button size="sm" variant="outline"
                                className={active ? 'text-rose-700' : 'text-emerald-700'}
                                onClick={() => toggleStatus(m)}
                                data-testid={`super-access-toggle-${m.email}`}>
                          {active ? <><UserX className="w-3.5 h-3.5 mr-1" /> Disable</>
                                  : <><UserCheck className="w-3.5 h-3.5 mr-1" /> Reactivate</>}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ── Onboarding checklist ────────────────────────────────── */}
      <Card>
        <CardHeader><CardTitle>Onboard a new consultant in 3 steps</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-2">
          <div><b>1.</b> Create the user above (Email + Password).</div>
          <div><b>2.</b> Send them the <b>Landing URL</b> and their credentials.</div>
          <div><b>3.</b> They log in and land directly in the existing Expansion
                       Intelligence workspace — no internal navigation knowledge required.</div>
        </CardContent>
      </Card>
    </div>
  );
}

function UrlRow({ label, url, onCopy, copied, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="text-xs uppercase tracking-wide text-slate-500 w-40 shrink-0">{label}</div>
      <div className="flex-1 font-mono text-sm bg-slate-50 border border-slate-200
                      rounded px-3 py-2 truncate"
           data-testid={`${testid}-value`}>{url}</div>
      <Button size="sm" variant="outline" onClick={onCopy}
              data-testid={`${testid}-copy`}>
        {copied ? <><Check className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Copied</>
                : <><Copy  className="w-3.5 h-3.5 mr-1" /> Copy</>}
      </Button>
    </div>
  );
}
